import { CalibrationPointsConfig } from 'features/calibration/sensors/types';
export const BASIC_FIELDS = ['BOL', 'AOL'] as const;
export type BasicField = typeof BASIC_FIELDS[number];

export const TEMPERATURE_FIELDS = ['roomT', 'roomF', 'highT', 'highTempF'] as const;
export type TemperatureField = typeof TEMPERATURE_FIELDS[number];

export const TEMPERATURE_KEYS = ['roomt', 'roomf', 'hight', 'hightempf'] as const;
export type TemperatureKey = typeof TEMPERATURE_KEYS[number];

export const BASIC_FIELD_KEYS = ['bol', 'aol'] as const;
export type BasicFieldKey = typeof BASIC_FIELD_KEYS[number];

export const ALL_KEYWORD_FIELDS = [
  ...BASIC_FIELD_KEYS,
  ...TEMPERATURE_KEYS,
] as const;
export type KeywordFieldKey = typeof ALL_KEYWORD_FIELDS[number];

export const CSV_CONSTANTS = {
  AOL_OFFSET: 1.66,
  POINT_MULTIPLIER: 3.5,
  INCREASING_OFFSET: 0.1,
  DECREASING_OFFSET: 0.2,
  SECOND_INCREASING_OFFSET: 0.3,
  DEFAULT_ROOM_TEMP: '25',
  DEFAULT_ROOM_FREQ: '1689.45',
} as const;



export const BULK_UPLOAD_CONSTANTS = {
  SENSORS_PER_FILE: 3,
  PROCESSING_DELAY_MS: 1500,
} as const;


export const VALIDATION_MESSAGES = {
  ALL_FILLED: "All required fields have been filled successfully!",
  FREQUENCY_ONLY: "Please fill Frequency data.",
  TEMPERATURE_ONLY: "Please fill Temperature data.",
  MISSING_FIELDS: (count: number, fields: string[]) =>
    `Missing ${count} required field(s): ${fields.join(", ")}`,
} as const;

export const GENERAL_MISSING_MESSAGES = new Set<string>([
  VALIDATION_MESSAGES.FREQUENCY_ONLY,
  VALIDATION_MESSAGES.TEMPERATURE_ONLY,
]);





export type FieldMap = Record<string, string>;


export const isBasicField = (field: string): field is BasicField => {
  return BASIC_FIELDS.includes(field as BasicField);
};


export const isTemperatureField = (field: string): field is TemperatureField => {
  return TEMPERATURE_FIELDS.includes(field as TemperatureField);
};


export const isTemperatureKey = (key: string): key is TemperatureKey => {
  const clean = key.toLowerCase().replace(/\s+/g, "");
  return TEMPERATURE_KEYS.includes(clean as TemperatureKey);
};


export const isBasicFieldKey = (key: string): key is BasicFieldKey => {
  const clean = key.toLowerCase().replace(/\s+/g, "");
  return BASIC_FIELD_KEYS.includes(clean as BasicFieldKey);
};


export const isKeywordField = (key: string): key is KeywordFieldKey => {
  const clean = key.toLowerCase().replace(/\s+/g, "");
  return ALL_KEYWORD_FIELDS.includes(clean as KeywordFieldKey);
};


export const createBasicFieldsObject = (): FieldMap => {
  return BASIC_FIELDS.reduce<FieldMap>((acc, field) => {
    acc[field] = "";
    return acc;
  }, {});
};


export const createTemperatureFieldsObject = (): FieldMap => {
  return TEMPERATURE_FIELDS.reduce<FieldMap>((acc, field) => {
    acc[field] = "";
    return acc;
  }, {});
};


export const clearCalibrationPointFields = (
  points: CalibrationPointsConfig,
  updates: FieldMap = {}
): FieldMap => {

  // Increasing points
  points.increasing.forEach(point => {
    const p = point.toString().replace(/\./g, '_');
    updates[`point_${p}_inc`] = "";
  });

  // Decreasing points
  points.decreasing.forEach(point => {
    if (Math.abs(point - 0) < 0.0001) {
      updates["point_0"] = "";
    } else {
      const p = point.toString().replace(/\./g, '_');
      updates[`point_${p}_dec`] = "";
    }
  });

  // Second increasing points
  points.secondIncreasing.forEach((point: number) => {
    const p = point.toString().replace(/\./g, '_');
    updates[`point_${p}_inc2`] = "";
  });

  return updates;
};


export const getCalibrationPointFieldNames = (
  points: CalibrationPointsConfig
): string[] => {

  const fields: string[] = [];

  points.increasing.forEach((point: number) => {
    const p = point.toString().replace(/\./g, '_');
    fields.push(`point_${p}_inc`);
  });

  points.decreasing.forEach((point: number) => {
    if (point === 0) {
      fields.push("point_0");
    } else {
      const p = point.toString().replace(/\./g, '_');
      fields.push(`point_${p}_dec`);
    }
  });

  points.secondIncreasing.forEach((point: number) => {
    const p = point.toString().replace(/\./g, '_');
    fields.push(`point_${p}_inc2`);
  });

  return fields;
};


export const getAllRequiredFieldNames = (
  points: CalibrationPointsConfig
): string[] => {
  return [
    ...BASIC_FIELDS,
    ...getCalibrationPointFieldNames(points),
    ...TEMPERATURE_FIELDS,
  ];
};
